/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * OnboardingPage — first-run setup wizard
 *
 * Steps (per product spec, 2026-04-23):
 *   1. Trading experience & goal       (writes Profile.trading_experience + notification_prefs.goal)
 *   2. Connect a broker account        (deep-links to /accounts; skippable)
 *   3. Risk profile per account        (depends on step 2 — risk profiles attach to broker accounts)
 *   4. Done                            (marks Profile.onboarding_completed_at, navigates to dashboard)
 *
 * Public pricing is intentionally omitted (Paxia is not yet public).
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  CheckCircle2,
  ChevronRight,
  ChevronLeft,
  Loader2,
  Plug,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  getUserProfile,
  updateUserProfile,
  fetchAccounts,
  fetchRiskSettings,
} from "../api/api";
import api from "../api/api";
import PageBackButton from "../components/PageBackButton";

const EXPERIENCE_OPTIONS = [
  { value: "BEGINNER", label: "Beginner", hint: "New to trading" },
  { value: "INTERMEDIATE", label: "Intermediate", hint: "Some live experience" },
  { value: "ADVANCED", label: "Advanced", hint: "Trade actively" },
  { value: "PROFESSIONAL", label: "Professional", hint: "Trade for a living" },
];

const GOAL_OPTIONS = [
  { value: "learn", label: "Learn & explore" },
  { value: "side_income", label: "Generate side income" },
  { value: "full_time", label: "Trade full-time" },
  { value: "automate", label: "Automate my strategies" },
];

const STEPS = [
  { id: 1, title: "About you", icon: Sparkles },
  { id: 2, title: "Connect a broker", icon: Plug },
  { id: 3, title: "Set risk profile", icon: ShieldCheck },
  { id: 4, title: "All set", icon: CheckCircle2 },
];

const STEP_DETAILS = {
  1: "Capture your experience and goal so the workspace defaults feel relevant from day one.",
  2: "Link a broker when you're ready. You can skip this and explore first without blocking the rest of setup.",
  3: "Set guardrails per account. Risk profiles attach to broker accounts, so this step comes after broker setup.",
  4: "Your workspace is ready. Head to the dashboard and start exploring your live trial access.",
};

const optionButtonClass = (selected) =>
  [
    "rounded-xl border px-3 py-3 text-left text-sm transition-all",
    selected
      ? "border-primary/40 bg-primary/10 text-textPrimary shadow-sm"
      : "border-borderDefault bg-backgroundBody/50 text-textSecondary hover:border-primary/25 hover:bg-backgroundBody/80 hover:text-textPrimary",
  ].join(" ");

function StepIndicator({ current }) {
  return (
    <ol className="flex flex-wrap items-center gap-2">
      {STEPS.map((s, idx) => {
        const done = current > s.id;
        const active = current === s.id;
        const Icon = s.icon;
        return (
          <li key={s.id} className="flex items-center gap-2 text-[11px] sm:text-xs">
            <div
              className={[
                "inline-flex items-center gap-2 rounded-full border px-3 py-2",
                active
                  ? "border-primary/40 bg-primary/10 text-textPrimary"
                  : done
                  ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-200"
                  : "border-borderDefault bg-backgroundSurface/80 text-textSecondary",
              ].join(" ")}
            >
              <span
                className={[
                  "inline-flex h-5 w-5 items-center justify-center rounded-full",
                  active
                    ? "bg-primary/15 text-primary"
                    : done
                    ? "bg-emerald-400/15 text-emerald-300"
                    : "bg-backgroundBody/70 text-textSecondary",
                ].join(" ")}
              >
                {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5" />}
              </span>
              <span className={active ? "font-medium text-textPrimary" : "font-medium"}>{s.title}</span>
            </div>
            {idx < STEPS.length - 1 && <span className="hidden h-px w-5 bg-borderDefault sm:block" />}
          </li>
        );
      })}
    </ol>
  );
}

export default function OnboardingPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [profile, setProfile] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Step 1 state
  const [experience, setExperience] = useState("");
  const [goal, setGoal] = useState("");

  // Step 3 state
  const [riskAccountId, setRiskAccountId] = useState("");
  const [riskPercent, setRiskPercent] = useState("0.5");
  const [maxOpenPositions, setMaxOpenPositions] = useState("3");
  const [maxDailyLoss, setMaxDailyLoss] = useState("3");

  // ── Initial load ──────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [prof, accs] = await Promise.all([
          getUserProfile(),
          fetchAccounts().catch(() => []),
        ]);
        if (cancelled) return;
        setProfile(prof || null);
        setAccounts(Array.isArray(accs) ? accs : accs?.results || []);
        // Pre-fill experience: prefer stored, else stash from signup.
        let initialExp = prof?.trading_experience || "";
        if (!initialExp) {
          try {
            initialExp = sessionStorage.getItem("paxia:onboarding:experience") || "";
          } catch { /* ignore */ }
        }
        setExperience(initialExp);
        const initialGoal = prof?.notification_prefs?.onboarding_goal || "";
        setGoal(initialGoal);
        // Resume on the saved step (capped at 3 — final screen is shown when done).
        const savedStep = Math.min(Math.max(prof?.onboarding_step || 1, 1), 3);
        setStep(savedStep);
      } catch {
        toast.error("Couldn't load your profile. Please try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const hasAccounts = accounts.length > 0;
  const currentStepMeta = STEPS[Math.min(step, STEPS.length) - 1];
  const CurrentStepIcon = currentStepMeta.icon;

  useEffect(() => {
    if (hasAccounts && !riskAccountId) {
      setRiskAccountId(accounts[0]?.id || "");
    }
  }, [hasAccounts, accounts, riskAccountId]);

  // ── Persist profile snapshot ──────────────────────────────────
  const persistProfile = useCallback(
    async (patch) => {
      try {
        const next = await updateUserProfile(patch);
        setProfile(next);
        return next;
      } catch (err) {
        console.warn("Profile update failed", err);
        toast.error("We couldn't save your changes — you can retry below.");
        throw err;
      }
    },
    []
  );

  // ── Step transitions ──────────────────────────────────────────
  const goNext = useCallback(
    async (overrides = {}) => {
      const nextStep = Math.min(step + 1, STEPS.length);
      setSaving(true);
      try {
        const prefs = {
          ...(profile?.notification_prefs || {}),
          onboarding_goal: goal || profile?.notification_prefs?.onboarding_goal || null,
        };
        const patch = {
          onboarding_step: nextStep,
          notification_prefs: prefs,
          ...overrides,
        };
        if (experience) patch.trading_experience = experience;
        await persistProfile(patch);
        setStep(nextStep);
      } catch {
        // toast already shown
      } finally {
        setSaving(false);
      }
    },
    [step, experience, goal, profile, persistProfile]
  );

  const goBack = useCallback(() => setStep((s) => Math.max(1, s - 1)), []);

  const completeOnboarding = useCallback(async () => {
    setSaving(true);
    try {
      await persistProfile({
        onboarding_step: STEPS.length,
        onboarding_completed_at: new Date().toISOString(),
      });
      try { sessionStorage.removeItem("paxia:onboarding:experience"); } catch { /* ignore */ }
      try { sessionStorage.setItem("paxia:onboarding-completed", "1"); } catch { /* ignore */ }
      toast.success("You're all set!");
      navigate("/dashboard", { replace: true });
    } catch {
      // toast already shown
    } finally {
      setSaving(false);
    }
  }, [navigate, persistProfile]);

  // ── Step 3: save risk settings ────────────────────────────────
  const saveRiskAndContinue = useCallback(async () => {
    if (!riskAccountId) {
      await goNext();
      return;
    }
    setSaving(true);
    try {
      // Check whether settings already exist; PUT to upsert.
      let exists = false;
      try {
        const existing = await fetchRiskSettings(riskAccountId);
        exists = !!existing && existing.id;
      } catch { exists = false; }
      const payload = {
        risk_percent: Number(riskPercent),
        max_open_positions: Number(maxOpenPositions),
        max_daily_loss: Number(maxDailyLoss),
      };
      const url = `/risk/settings/${riskAccountId}/`;
      if (exists) {
        await api.patch(url, payload);
      } else {
        await api.post(url, payload);
      }
      toast.success("Risk profile saved.");
      await goNext();
    } catch (err) {
      console.warn("Risk save failed", err);
      toast.error("Couldn't save risk settings. You can adjust them later from the account page.");
      await goNext();
    } finally {
      setSaving(false);
    }
  }, [riskAccountId, riskPercent, maxOpenPositions, maxDailyLoss, goNext]);

  // ── Render ────────────────────────────────────────────────────
  const card = useMemo(() => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-[#4FA9A1]" />
        </div>
      );
    }

    if (step === 1) {
      return (
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-textPrimary">Tell us about you</h2>
            <p className="text-sm text-textSecondary mt-1">
              We'll use this to tailor your dashboard, suggested strategies, and risk defaults.
            </p>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wide text-textSecondary mb-2">
              Trading experience
            </label>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {EXPERIENCE_OPTIONS.map((opt) => (
                <button
                  type="button"
                  key={opt.value}
                  onClick={() => setExperience(opt.value)}
                  className={optionButtonClass(experience === opt.value)}
                >
                  <div className="font-medium text-textPrimary">{opt.label}</div>
                  <div className="text-xs opacity-80">{opt.hint}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wide text-textSecondary mb-2">
              What's your main goal? <span className="opacity-60 normal-case">(optional)</span>
            </label>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {GOAL_OPTIONS.map((opt) => (
                <button
                  type="button"
                  key={opt.value}
                  onClick={() => setGoal(opt.value)}
                  className={optionButtonClass(goal === opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      );
    }

    if (step === 2) {
      return (
        <div className="space-y-5">
          <div>
            <h2 className="text-lg font-semibold text-textPrimary">Connect a broker account</h2>
            <p className="text-sm text-textSecondary mt-1">
              Paxia trades through your existing broker. You can connect MT5, cTrader,
              Interactive Brokers, or Binance — or skip and explore in demo mode first.
            </p>
          </div>

          <div className="rounded-xl border border-borderDefault p-4 bg-backgroundBody/50 shadow-sm">
            {hasAccounts ? (
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                <div>
                  <div className="text-sm text-textPrimary font-medium">
                    {accounts.length} account{accounts.length === 1 ? "" : "s"} connected
                  </div>
                  <div className="text-xs text-textSecondary mt-0.5">
                    You can add more anytime from the Accounts page.
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="text-sm text-textPrimary font-medium">No accounts yet</div>
                  <div className="text-xs text-textSecondary mt-0.5">
                    Open the Accounts page in a new tab and link your broker, then come back here.
                  </div>
                </div>
                <a
                  href="/accounts"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-1 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-textAccent transition hover:bg-primary/90"
                >
                  <Plug className="w-4 h-4" />
                  Connect
                </a>
              </div>
            )}
          </div>
        </div>
      );
    }

    if (step === 3) {
      return (
        <div className="space-y-5">
          <div>
            <h2 className="text-lg font-semibold text-textPrimary">Set your risk profile</h2>
            <p className="text-sm text-textSecondary mt-1">
              Risk rules are applied per account. You can fine-tune everything later in the
              risk settings page.
            </p>
          </div>

          {!hasAccounts ? (
            <div className="rounded-xl border border-warning/30 bg-warning/10 p-4 text-sm text-warning">
              You haven't connected an account yet. Skip this step for now — risk profiles
              attach to broker accounts and can be set the moment you connect one.
            </div>
          ) : (
            <>
              <div>
                <label className="block text-xs uppercase tracking-wide text-textSecondary mb-1.5">
                  Account
                </label>
                <select
                  value={riskAccountId}
                  onChange={(e) => setRiskAccountId(e.target.value)}
                  className="w-full rounded-lg border border-borderDefault bg-backgroundInput text-textPrimary p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                >
                  {accounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name || a.platform} {a.simple_id ? `· #${a.simple_id}` : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <NumberField
                  label="Risk per trade (%)"
                  value={riskPercent}
                  onChange={setRiskPercent}
                  step="0.1"
                  min="0.05"
                  max="5"
                />
                <NumberField
                  label="Max open positions"
                  value={maxOpenPositions}
                  onChange={setMaxOpenPositions}
                  step="1"
                  min="1"
                  max="50"
                />
                <NumberField
                  label="Max daily loss (%)"
                  value={maxDailyLoss}
                  onChange={setMaxDailyLoss}
                  step="0.1"
                  min="0.5"
                  max="20"
                />
              </div>
            </>
          )}
        </div>
      );
    }

    // Step 4
    return (
      <div className="space-y-4 py-4 text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/15">
          <CheckCircle2 className="w-6 h-6 text-emerald-400" />
        </div>
        <h2 className="text-lg font-semibold text-textPrimary">You're all set!</h2>
        <p className="text-sm text-textSecondary max-w-md mx-auto">
          Your free trial is active. Explore the dashboard, build your first strategy,
          or chat with the AI assistant in the top-right of every page.
        </p>
      </div>
    );
  }, [
    loading, step, experience, goal, hasAccounts, accounts,
    riskAccountId, riskPercent, maxOpenPositions, maxDailyLoss,
  ]);

  // ── Footer actions ────────────────────────────────────────────
  const primaryAction = useMemo(() => {
    if (step === 1) {
      return {
        label: "Continue",
        onClick: () => goNext(),
        disabled: !experience,
      };
    }
    if (step === 2) {
      return {
        label: hasAccounts ? "Continue" : "Skip for now",
        onClick: () => goNext(),
        disabled: false,
      };
    }
    if (step === 3) {
      return {
        label: hasAccounts ? "Save & continue" : "Skip for now",
        onClick: hasAccounts ? saveRiskAndContinue : () => goNext(),
        disabled: false,
      };
    }
    return {
      label: "Go to dashboard",
      onClick: completeOnboarding,
      disabled: false,
    };
  }, [step, experience, hasAccounts, goNext, saveRiskAndContinue, completeOnboarding]);

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6 px-4 pb-8 sm:px-6">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex items-start gap-3">
          <PageBackButton
            label="Back to Dashboard"
            to="/dashboard"
            preferHistory={false}
            hideLabelOnMobile
          />
          <div className="space-y-3">
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/25 bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
              <Sparkles className="h-3.5 w-3.5" />
              Guided setup
            </div>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight text-textPrimary">Welcome to Paxia</h1>
              <p className="mt-1 max-w-2xl text-sm text-textSecondary">
                A few quick steps and you're trading. You can skip any step and come back later.
              </p>
            </div>
          </div>
        </div>

        <Card className="w-full max-w-sm bg-backgroundSurface/90">
          <CardContent className="p-4 pt-4">
            <div className="text-xs uppercase tracking-wide text-textSecondary">Trial access</div>
            <div className="mt-1 text-sm font-semibold text-textPrimary">Your trial is already active</div>
            <p className="mt-1 text-xs text-textSecondary">
              No payment details are required to finish setup. Complete only the parts you need.
            </p>
          </CardContent>
        </Card>
      </header>

      <StepIndicator current={step} />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_280px] lg:items-start">
        <Card className="overflow-hidden">
          <div className="h-1.5 bg-gradient-to-r from-primary/70 via-primary to-primary/40" />
          <CardContent className="p-5 pt-5 sm:p-6">
            {card}
          </CardContent>

          <div className="flex flex-col gap-3 border-t border-borderDefault bg-backgroundBody/35 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={goBack}
              disabled={step === 1 || saving}
              className="justify-start gap-1 text-textSecondary hover:text-textPrimary sm:justify-center"
            >
              <ChevronLeft className="h-4 w-4" />
              Back
            </Button>

            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center">
              {step < STEPS.length && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={completeOnboarding}
                  disabled={saving}
                  className="text-textSecondary hover:text-textPrimary"
                >
                  Skip setup
                </Button>
              )}
              <Button
                type="button"
                size="md"
                onClick={primaryAction.onClick}
                disabled={primaryAction.disabled || saving}
                className="gap-1.5"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                {primaryAction.label}
                {!saving && step < STEPS.length && <ChevronRight className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardContent className="p-5 pt-5">
              <div className="text-xs uppercase tracking-wide text-textSecondary">Current step</div>
              <div className="mt-3 flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <CurrentStepIcon className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-textPrimary">Step {step} of {STEPS.length}</div>
                  <div className="mt-0.5 text-sm text-textPrimary">{currentStepMeta.title}</div>
                  <p className="mt-2 text-xs leading-5 text-textSecondary">{STEP_DETAILS[step]}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-5 pt-5">
              <div className="text-xs uppercase tracking-wide text-textSecondary">What carries forward</div>
              <div className="mt-3 space-y-3 text-sm text-textSecondary">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-400" />
                  <span>Your experience helps tailor defaults across the workspace.</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-400" />
                  <span>Broker-linked accounts unlock risk profiles and live automation.</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-400" />
                  <span>You can revisit and change everything later from the app.</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function NumberField({ label, value, onChange, step = "1", min, max }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wide text-textSecondary mb-1.5">{label}</span>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        step={step}
        min={min}
        max={max}
        className="w-full rounded-lg border border-borderDefault bg-backgroundInput px-3 py-2.5 text-sm text-textPrimary focus:outline-none focus:ring-2 focus:ring-primary/40"
      />
    </label>
  );
}
